/**
 * File view component.
 * @module components/theme/View/FileView
 */

import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';
import baseMessages from '@fhnw/messages/base';

import { ButtonGroup } from '@designsystem/stories/03-molecules/ButtonGroup';

/**
 * File view component class.
 * @function FileView
 * @params {object} content Content object.
 * @returns {string} Markup of the component.
 */
const FileView = ({ content }) => {
  function getReadableFileSizeString(fileSizeInBytes) {
    var i = -1;
    var byteUnits = [' kB', ' MB', ' GB', ' TB', 'PB', 'EB', 'ZB', 'YB'];
    do {
      fileSizeInBytes /= 1024;
      i++;
    } while (fileSizeInBytes > 1024);

    return Math.max(fileSizeInBytes, 0.1).toFixed(1) + byteUnits[i];
  }

  const intl = useIntl();

  const buttons = [];

  if (content.file?.download) {
    buttons.push({
      type: 'primary',
      label: intl.formatMessage(baseMessages.DownloadDocument),
      href: flattenToAppURL(content.file.download),
    });

    if (
      content.file['content-type'] &&
      content.file['content-type'] === 'application/pdf'
    ) {
      buttons.push({
        type: 'secondary',
        label: intl.formatMessage(baseMessages.OpenDocumentInBrowser),
        href: flattenToAppURL(
          content.file.download.replace('@@download', '@@display-file'),
        ),
      });
    }
  }

  return (
    <section id="file-view" className="page__section page__section--followup">
      {buttons && <ButtonGroup items={buttons} />}
      {content.file?.filename && (
        <p>
          {content.file.filename !== content.title && content.file.filename}
          {content.file.size && (
            <span>&nbsp;({getReadableFileSizeString(content.file.size)})</span>
          )}
        </p>
      )}
    </section>
  );
};

/**
 * Property types.
 * @property {Object} propTypes Property types.
 * @static
 */
FileView.propTypes = {
  content: PropTypes.shape({
    title: PropTypes.string,
    description: PropTypes.string,
    file: PropTypes.shape({
      download: PropTypes.string,
      filename: PropTypes.string,
    }),
  }).isRequired,
};

export default FileView;
